import React, { useState, useEffect, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Axios from "axios";
import ConfigFileGenerator from "./GenerateConfigFile";
import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Switch,
} from "@mui/material";
import { Toggle, Dropdown, SpinButton, PrimaryButton, DefaultButton } from "@fluentui/react";
import { FaFileCsv, FaRegFilePdf } from "react-icons/fa";
import "../Styles/CloudProLoopTable.css";

import smokeIcon from "../Assets/icons/smoke.jpg";
import thermalIcon from "../Assets/icons/Heat.jpg";
import multiIcon from "../Assets/icons/Multi.jpg";
import outputIcon from "../Assets/icons/output.jpg";
import inIcon from "../Assets/icons/input.jpg";
import callpointIcon from "../Assets/icons/callpoint.jpg";
import sirenIcon from "../Assets/icons/siren.jpg";
import flasherIcon from "../Assets/icons/flasher.jpg";

function prepareCfgExport(content, panelNumber) {
  const document = typeof content === "string" ? JSON.parse(content) : content;
  const panels = [];
  const visit = (value) => {
    if (Array.isArray(value)) return value.forEach(visit);
    if (!value || typeof value !== "object") return;
    if (value.GeneralParameters && Array.isArray(value.ArgusTablePr)) {
      panels.push(value);
      return;
    }
    Object.values(value).forEach(visit);
  };
  visit(document);
  const matches = panels.filter(
    (p) => Number(p.GeneralParameters.vtnetPanelAddress) === Number(panelNumber)
  );
  const source = matches.length === 1 ? matches[0] : panels.length === 1 ? panels[0] : null;
  if (!source) throw new Error("Cannot identify the selected panel in the exported configuration.");
  const config = { ...source };
  const aliases = {
    OutputZones: "OutputZone",
    ZoneTable: "Zone_Table",
    DaylightSaving: "Daylightsaving",
  };
  for (const [target, alias] of Object.entries(aliases)) {
    if (
      source[target] != null &&
      source[alias] != null &&
      JSON.stringify(source[target]) !== JSON.stringify(source[alias])
    ) {
      throw new Error(`Conflicting config fields: ${target} / ${alias}`);
    }
    config[target] = source[target] ?? source[alias];
  }
  for (const name of [
    "OutputZones",
    "ZoneTable",
    "GeneralParameters",
    "Codes",
    "DaylightSaving",
    "Holidays",
    "TimeIntervals",
    "WeeklyTrends",
    "FlogicPr",
    "ArgusTablePr",
    "VTNet",
  ]) {
    if (config[name] == null) throw new Error(`Config is missing ${name}; export cancelled.`);
  }
  return config;
}
//Api helper
const CE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/cause-effect-workflow`;
const MAX_MODULES_PER_OUTPUT_ZONE = 8;

const getRevision = () => {
  const r = Number(sessionStorage.getItem("causeEffectRevision"));
  return Number.isFinite(r) && r > 0 ? r : undefined;
};

const saveRevision = (rev) => {
  if (rev != null) sessionStorage.setItem("causeEffectRevision", String(rev));
};

const cePost = async (path, projectId, body = {}, opts = {}) => {
  const { withRevision = true, ...axiosOpts } = opts;
  const res = await Axios.post(
    `${CE_BASE}${path}`,
    {
      email: localStorage.getItem("userEmail"),
      projectId,
      ...(withRevision ? { expectedRevision: getRevision() } : {}),
      ...body,
    },
    axiosOpts
  );
  if (res.data && typeof res.data === "object" && res.data.revision != null) {
    saveRevision(res.data.revision);
  }
  return res;
};

const apiError = (err, fallback) => {
  const d = err?.response?.data;
  if (err?.response?.status === 409) {
    return "The configuration changed elsewhere. Reload the devices and try again.";
  }
  if (Array.isArray(d?.errors) && d.errors.length) {
   
    const one = (e) => {
      if (typeof e === "string") return e;
      const msg = e?.message || e?.msg || e?.error || "";
      const code = e?.code ? ` (${e.code})` : "";
      const dev = e?.deviceId ? ` [${e.deviceId}]` : "";
      return msg ? `${msg}${code}${dev}` : JSON.stringify(e);
    };
    const shown = d.errors.slice(0, 3).map(one).join(" | ");
    const rest = d.errors.length - 3;
    return rest > 0
      ? `${d.errors.length} problems. First: ${shown} ... (+${rest} more, see console)`
      : shown;
  }
  const text =
    d?.msg || d?.message || (typeof d?.error === "string" ? d.error : d?.error?.message) || fallback;
  const code = d?.code || d?.error?.code;
  return code ? `${text} (${code})` : text;
};
//Generate logice function
const ALARM_EVENT = 1 * 65536;
const ZONE_OPERAND_BASE = 3856;

const emptyFlogic = () => ({
  programmed: 0,
  delay: 0,
  duration: 0,
  fLogicAss: 0,
  outputZones: [0, 0, 0, 0],
  c1Level: [
    { c1Operat: 5, c2Operand: [0, 0, 0, 0], c2Operator: [5, 0, 0, 0] },
    { c1Operat: 0, c2Operand: [0, 0, 0, 0], c2Operator: [0, 0, 0, 0] },
    { c1Operat: 0, c2Operand: [0, 0, 0, 0], c2Operator: [0, 0, 0, 0] },
    { c1Operat: 0, c2Operand: [0, 0, 0, 0], c2Operator: [0, 0, 0, 0] },
  ],
});

const allocationsToFlogicPr = (allocations, length, hostPanelNumber) => {
  const list = Array.from({ length }, emptyFlogic);
  (allocations || []).forEach((a) => {
    if (
      hostPanelNumber != null &&
      a.hostPanelNumber != null &&
      Number(a.hostPanelNumber) !== Number(hostPanelNumber)
    ) {
      return;
    }
    const i = Number(a.logicFunctionIndex) - 1;
    if (!Number.isInteger(i) || i < 0 || i >= length) return;

    const oz = [...(a.outputZoneIndices || [])].slice(0, 4);
    while (oz.length < 4) oz.push(0);

    list[i] = {
      programmed: 1,
      delay: a.delaySeconds || 0,
      duration: 0,
      fLogicAss: 0,
      outputZones: oz,
      c1Level: [
        {
          c1Operat: 5,
          c2Operand: [ZONE_OPERAND_BASE + Number(a.zoneIndex) + ALARM_EVENT, 0, 0, 0],
          c2Operator: [5, 0, 0, 0],
        },
        { c1Operat: 6, c2Operand: [0, 0, 0, 0], c2Operator: [0, 0, 0, 0] },
        { c1Operat: 0, c2Operand: [0, 0, 0, 0], c2Operator: [0, 0, 0, 0] },
        { c1Operat: 0, c2Operand: [0, 0, 0, 0], c2Operator: [0, 0, 0, 0] },
      ],
    };
  });
  return list;
};

const writeGeneratedLogicToProject = (allocations, hostPanelNumber, projectId) => {
  try {
    const proj = JSON.parse(sessionStorage.getItem("selectedProject"));
    const cfg = proj?.panel?.VT5000_CONFIG;
    if (!cfg) return false;
    const key = cfg.FlogicPr ? "FlogicPr" : cfg.flogicpr ? "flogicpr" : "FlogicPr";
    const length = (cfg[key] || []).length || 64;
    const arr = allocationsToFlogicPr(allocations, length, hostPanelNumber);

    cfg[key] = arr;
    proj.__generatedApplied = true;
    sessionStorage.setItem("selectedProject", JSON.stringify(proj));
    localStorage.setItem(`generatedFlogicPr:${projectId}`, JSON.stringify(arr));
    return true;
  } catch (e) {
    console.error("Could not update LF page data", e);
    return false;
  }
};

const flag = (v) => Number(v) === 1 || v === true;
const positive = (v) => Number(v) > 0;
const num = (v) => (v === undefined || v === null || v === "" ? 0 : v);

const cleanName = (n) =>
  typeof n === "string" ? n.replace(/[\x00-\x1F\x7F]/g, "").trim() : "";

const zoneIndexes = (list) =>
  (Array.isArray(list) ? list : [])
    .map((z) => (typeof z === "object" && z !== null ? z.outputZoneIndex : z))
    .map(Number)
    .filter((n) => Number.isInteger(n));

const parseZoneText = (text) =>
  String(text || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number);

const baseDeviceMeta = (d) => {
  const st = d?.sourceType || {};
  const src = { ...d, ...st };
  const deviceType = Number(src.deviceType ?? 0);
  const lastTipo = Number(src.lastTipo ?? 0);
  const is = (field) => Number(src[field]) === 1;
  const base = is("sounderBase") ? " (Sounder Base)" : "";
  const typeStr = String(d?.type || "").trim().toUpperCase();

  if (deviceType === 0 && lastTipo === 1)
    return { icon: thermalIcon, label: `Thermal Detector${base}`, kind: "Thermal", isOutput: false };
  if (deviceType === 0 && lastTipo === 0 && (is("isMultiSensor") || typeStr === "SMOKE THERM."))
    return { icon: multiIcon, label: `Multi Detector${base}`, kind: "Smoke Therm.", isOutput: false };
  if (deviceType === 0)
    return { icon: smokeIcon, label: `Smoke Detector${base}`, kind: "Smoke", isOutput: false };

  if (deviceType === 1 && lastTipo === 3) {
    if (is("isCallPoint") || typeStr === "MCP" || typeStr.includes("CALL"))
      return { icon: callpointIcon, label: "Call Point", kind: "Call Point", isOutput: false };
    if (is("isGas") || typeStr === "GAS")
      return { icon: null, label: "Gas", kind: "Gas", isOutput: false };
    return { icon: inIcon, label: "Input", kind: "Input", isOutput: false };
  }
  if (deviceType === 1 && lastTipo === 4) {
    if (is("isFlasher") || typeStr.includes("FLASH"))
      return { icon: flasherIcon, label: "Flasher", kind: "Flasher", isOutput: true };
    if (is("isSounder") || typeStr.includes("SIREN") || typeStr.includes("SOUNDER"))
      return { icon: sirenIcon, label: "Siren", kind: "Siren", isOutput: true };
    if (is("isRepeater") || typeStr.includes("REP"))
      return { icon: null, label: "Rep.Led", kind: "Rep.Led", isOutput: true };
    return { icon: outputIcon, label: "Output", kind: "Output", isOutput: true };
  }
  if (deviceType === 1 && lastTipo === 5)
    return { icon: null, label: "XPander", kind: "XPander", isOutput: false, noMapping: true };

  return { icon: null, label: "Unknown", kind: "Unknown", isOutput: false, noMapping: true };
};

const getDeviceMeta = (d) => {
  const meta = baseDeviceMeta(d);

  if (d?.role === "unsupported") return { ...meta, noMapping: true, isOutput: false };
  if (d?.role === "output") return { ...meta, isOutput: true, noMapping: false };
  if (d?.role === "input") return { ...meta, isOutput: false, noMapping: false };
  return meta;
};


const DAY_NIGHT = ["24H", "Night", "Fixed"];
const SENSITIVITY = ["Low", "Medium Low", "Medium High", "High"];
const DISABLE_PART = ["None", "Thermal", "Smoke"];

const DISABLE_OPTIONS = DISABLE_PART.map((text, key) => ({ key, text }));
const DAY_NIGHT_OPTIONS = DAY_NIGHT.map((text, key) => ({ key, text }));
const SENSITIVITY_OPTIONS = SENSITIVITY.map((text, key) => ({ key, text }));
const VERIFY_OPTIONS = Array.from({ length: 30 }, (_, i) => ({
  key: i + 1,
  text: ((i + 1) * 0.5).toFixed(1),
}));
const PREALARM_OPTIONS = Array.from({ length: 256 }, (_, i) => ({ key: i, text: String(i) }));

const SETTINGS = [
  "isRadio",
  "sounderBase",
  "evacuationEnabled",
  "alarmVerification",
  "technical",
  "technicalFault",
  "technicalRestore",
  "prealarm",
];

const decodeSensSecDis = (d) => {
  switch (Number(d.sensSecDis)) {
    case 7: return [1, 1];
    case 6: return [2, 1];
    case 5: return [0, 1];
    case 4: return [1, 2];
    case 3: return [2, 2];
    case 2: return [0, 2];
    case 1: return [1, 0];
    default: return Number(d.secDisEn) === 0 ? [0, 0] : [2, 0];
  }
};

const encodeSensSecDis = (day, night) => {
  if (night === 1) return [day === 0 ? 5 : day === 1 ? 7 : 6, 1];
  if (night === 2) return [day === 0 ? 2 : day === 1 ? 4 : 3, 1];
  if (day === 0) return [0, 0];
  if (day === 1) return [1, 1];
  return [0, 1];
};

const toDraft = (d) => {
  const [secDay, secNight] = decodeSensSecDis(d);
  return {
    name: cleanName(d.name),
    isRadio: flag(d.isRadio),
    sounderBase: flag(d.sounderBase),
    evacuationEnabled: flag(d.evacuationEnabled),
    volume: Number(num(d.volume)),
    tone: Number(num(d.tone)),
    alarmVerification: flag(d.alarmVerification),
    alarmVerificationDelay: Number(num(d.alarmVerificationDelay)),
    technical: positive(d.technical),
    technicalFault: positive(d.technicalFault),
    technicalRestore: positive(d.technicalRestore),
    prealarm: flag(d.prealarm),
    preAllTemp: Number(num(d.preAllTemp)),
    dayNight: Number(num(d.dayNight)),
    detectorDaySensitivity: Number(num(d.detectorDaySensitivity)),
    intendedDisChannel: Number(num(d.intendedDisChannel)),
    detectorSensitivityWeekCalendar: Number(num(d.detectorSensitivityWeekCalendar)),
    detectorMultiSenseWeekCalendar: Number(num(d.detectorMultiSenseWeekCalendar)),
    secDay,
    secNight,
  };
};


const ROW_STYLE = { display: "flex", alignItems: "center", gap: 10, height: 32 };
const LABEL_STYLE = { fontSize: 14, color: "#333", minWidth: 150 };
const BOX_STYLE = {
  minWidth: 70,
  padding: "3px 10px",
  border: "1px solid #ccc",
  borderRadius: 2,
  background: "#fff",
  fontSize: 14,
  textAlign: "center",
};

const TOGGLE_STYLES = {
  root: { marginBottom: 0, padding: 0 },
  label: { display: "none" },
  pill: { marginTop: 0, alignSelf: "center" },
  thumb: { marginTop: 0 },
};
const SPIN_STYLES = {
  root: { marginBottom: 0, padding: 0, width: "100%" },
  spinButtonWrapper: { height: 32, display: "flex", alignItems: "center" },
  input: { textAlign: "center", fontSize: 14 },
};
const dropStyles = (w) => ({
  root: { marginBottom: 0, padding: 0, width: "100%" },
  title: { height: 32, lineHeight: "32px", textAlign: "center" },
  dropdown: { width: w },
});


const EditToggle = ({ label, checked, onChange }) => (
  <div style={ROW_STYLE}>
    <span style={LABEL_STYLE}>{label}:</span>
    <Toggle
      styles={TOGGLE_STYLES}
      checked={!!checked}
      onChange={(e, c) => onChange(!!c)}
      onText="Yes"
      offText="No"
    />
  </div>
);

const EditDropdown = ({ label, options, value, onChange, width = 120, disabled, placeholder }) => (
  <div style={ROW_STYLE}>
    <span style={LABEL_STYLE}>{label}:</span>
    <div style={{ width }}>
      <Dropdown
        styles={dropStyles(width)}
        options={options}
        selectedKey={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e, o) => o && onChange(o.key)}
      />
    </div>
  </div>
);

const EditSpin = ({ label, value, min, max, onChange }) => (
  <div style={ROW_STYLE}>
    <span style={LABEL_STYLE}>{label}:</span>
    <div style={{ width: 100 }}>
      <SpinButton
        min={min}
        max={max}
        value={String(value)}
        styles={SPIN_STYLES}
        onChange={(e, v) => {
          const n = parseInt(v, 10);
          if (!isNaN(n) && n >= min && n <= max) onChange(n);
        }}
      />
    </div>
  </div>
);


const ToggleView = ({ label, on, dim }) => (
  <div style={{ ...ROW_STYLE, opacity: dim ? 0.5 : 1 }}>
    <span style={LABEL_STYLE}>{label}:</span>
    <Switch size="small" checked={!!on} disabled />
    <span style={{ fontSize: 14 }}>{on ? "Yes" : "No"}</span>
  </div>
);

const ValueView = ({ label, value, dim }) => (
  <div style={{ ...ROW_STYLE, opacity: dim ? 0.5 : 1 }}>
    <span style={LABEL_STYLE}>{label}:</span>
    <span style={BOX_STYLE}>{String(value)}</span>
  </div>
);

const Group = ({ title, children }) => (
  <>
    <div style={{ fontWeight: 600, margin: "14px 0 6px" }}>{title}</div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        columnGap: 24,
      }}
    >
      {children}
    </div>
  </>
);

const ZoneBlock = ({ index, zone, reason, onSaveZone, busy }) => {
  if (!zone) {
    return <div>Zone {index} was not found in the zone table.</div>;
  }

  const update = (field, value) => {
    onSaveZone(index - 1, {
      [field]: value,
      programmed: 1,
    });
  };

  const inputStyle = {
    width: 100,
    boxSizing: "border-box",
    padding: "5px 6px",
  };

  const numberField = (label, field, min = 0, max = 255) => (
    <label style={ROW_STYLE}>
      <span style={LABEL_STYLE}>{label}:</span>
      <input
        type="number"
        min={min}
        max={max}
        value={num(zone[field])}
        disabled={busy}
        style={inputStyle}
        onChange={(e) => {
          const value = e.target.value;
          if (value === "") return;

          const parsed = Number(value);
          if (Number.isInteger(parsed) && parsed >= min && parsed <= max) {
            update(field, parsed);
          }
        }}
      />
    </label>
  );

  const toggleField = (label, field) => (
    <EditToggle
      label={label}
      checked={flag(zone[field])}
      onChange={(checked) => update(field, checked ? 1 : 0)}
    />
  );

  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: 6,
        background: "#fff",
        padding: "8px 12px",
        marginTop: 8,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14 }}>
        Zone {index}
        <span
          style={{
            fontWeight: 400,
            color: "#777",
            fontSize: 12,
            marginLeft: 8,
          }}
        >
          ({reason})
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          columnGap: 24,
        }}
      >
        <label style={ROW_STYLE}>
          <span style={LABEL_STYLE}>Zone name:</span>
          <input
            value={zone.name || ""}
            disabled={busy}
            style={inputStyle}
            onChange={(e) => update("name", e.target.value)}
          />
        </label>

        {toggleField("All System", "allSystem")}
        {numberField("Loop Selected", "loop")}
        {numberField("Weekly Trend Autoexclusion", "weeklyTrendDisablement")}

        {toggleField("Alarm", "alarm")}
        {numberField("Alarm Coincidence", "alarmCoincidence", 1, 30)}

        {toggleField("Fault", "fault")}
        {numberField("Fault Coincidence", "faultCoincidence", 1, 30)}

        {toggleField("Technological", "technical")}
        {numberField("Technological Coincidence", "technicalCoincidence", 1, 30)}
      </div>
    </div>
  );
};


const DeviceDetails = ({
  data: row,
  zoneTable = [],
  weeklyTrends = [],
  onSave,
   onSaveZone,
  busy,
  devEdits = {},
  patchDevice,
  clearDevice,
}) => {
  const d = row.raw || {};
  const initial = toDraft(d);
  const draft = { ...initial, ...(devEdits[row.deviceId] || {}) };
  const set = (patch) => patchDevice?.(row.deviceId, patch);

  const k = row.meta.kind;
  const isSmoke = k === "Smoke";
  const isThermal = k === "Thermal";
  const isSmokeTherm = k === "Smoke Therm.";
  const isDetector = isSmoke || isThermal || isSmokeTherm;
  const isSmokeLike = isSmoke || isSmokeTherm;
  const isInputLike = k === "Input" || k === "Call Point" || k === "Gas";
  const isSiren = k === "Siren";
  const isOutputLike = k === "Output" || k === "Flasher" || isSiren || k === "Rep.Led";
  const canBeInZone = isDetector || isInputLike;
  const panelVersion = sessionStorage.getItem("panelVersion1") || "2.0.F7";

 
  const changes = {};
  Object.keys(draft).forEach((key) => {
    if (key === "secDay" || key === "secNight") return;
    if (draft[key] !== initial[key]) {
      changes[key] = SETTINGS.includes(key) ? (draft[key] ? 1 : 0) : draft[key];
    }
  });
  if (draft.secDay !== initial.secDay || draft.secNight !== initial.secNight) {
    const [s, e] = encodeSensSecDis(draft.secDay, draft.secNight);
    changes.sensSecDis = s;
    changes.secDisEn = e;
  }
  const dirty = Object.keys(changes).length > 0;

  const trendOptions = (cur) => {
    const opts = (weeklyTrends || [])
      .map((w, i) => ({ key: i + 1, text: String(i + 1), disabled: Number(w?.programmed) !== 1 }))
      .filter((o) => !o.disabled || o.key === cur);
    if (!opts.some((o) => o.key === cur)) opts.unshift({ key: cur, text: String(cur), disabled: true });
    return opts;
  };

 
  const zoneRefs = [];
  const seen = new Set();
  const addZone = (n, reason) => {
    if (!Number.isInteger(n) || n <= 0 || seen.has(n)) return;
    seen.add(n);
    zoneRefs.push({ n, reason });
  };
  [d.zone, d.zone2, d.zone3, d.zone4].map(Number).forEach((n) => addZone(n, "assigned to device"));
  if (canBeInZone) {
    (zoneTable || []).forEach((z, i) => {
      if (!z || !flag(z.programmed)) return;
      if (flag(z.allSystem)) addZone(i + 1, "All System");
      else if (Number(z.loop) > 0 && Number(z.loop) === Number(d.loopNumber))
        addZone(i + 1, `whole loop ${z.loop}`);
    });
  }
  zoneRefs.sort((a, b) => a.n - b.n);

  return (
    <div style={{ padding: 15, background: "#f9f9f9" }}>
     <div style={{ fontSize: 12, color: "#777", marginBottom: 4 }}>
  Device settings and zone settings are editable.
  Zone changes are saved automatically.
</div>

      <Group title={`Device settings (${row.meta.label})`}>
      
      
        <EditToggle label="Radio" checked={draft.isRadio} onChange={(v) => set({ isRadio: v })} />

        
        {isDetector && (
          <EditToggle
            label="Slave Sounder"
            checked={draft.sounderBase}
            onChange={(v) => set({ sounderBase: v })}
          />
        )}

        {(isOutputLike || (isDetector && draft.sounderBase)) && (
          <EditToggle
            label="Activate Evacuation"
            checked={draft.evacuationEnabled}
            onChange={(v) => set({ evacuationEnabled: v })}
          />
        )}

        {isSiren && (
          <EditSpin label="Volume" value={draft.volume} min={0} max={31} onChange={(v) => set({ volume: v })} />
        )}
        {isSiren && (
          <EditSpin label="Tone" value={draft.tone} min={0} max={4} onChange={(v) => set({ tone: v })} />
        )}

       
        {(isDetector || isInputLike) && (
          <EditToggle
            label="Verify Alarms"
            checked={draft.alarmVerification}
            onChange={(v) =>
              set({
                alarmVerification: v,
              
                ...(v && panelVersion !== "2.0.F7"
                  ? { alarmVerificationDelay: 5 }
                  : v && !draft.alarmVerificationDelay
                  ? { alarmVerificationDelay: 1 }
                  : {}),
              })
            }
          />
        )}
        {(isDetector || isInputLike) && (
          <EditDropdown
            label="Verify Minutes"
            width={100}
            placeholder="-"
            options={VERIFY_OPTIONS}
            value={draft.alarmVerificationDelay > 0 ? draft.alarmVerificationDelay : null}
            disabled={!draft.alarmVerification}
            onChange={(v) => set({ alarmVerificationDelay: v })}
          />
        )}

        {isInputLike && (
          <EditToggle
            label="Technological"
            checked={draft.technical}
            onChange={(v) => set({ technical: v })}
          />
        )}
        {isInputLike && draft.technical && (
          <EditToggle
            label="Fault"
            checked={draft.technicalFault}
            onChange={(v) => set({ technicalFault: v })}
          />
        )}
        {isInputLike && draft.technical && (
          <EditToggle
            label="Self Restore"
            checked={draft.technicalRestore}
            onChange={(v) => set({ technicalRestore: v })}
          />
        )}
        {isInputLike && draft.technical && (
          <EditToggle label="Prealarm" checked={draft.prealarm} onChange={(v) => set({ prealarm: v })} />
        )}
        {isInputLike && draft.technical && (
          <EditDropdown
            label="Prealarm Period"
            width={100}
            options={PREALARM_OPTIONS}
            value={draft.preAllTemp}
            onChange={(v) => set({ preAllTemp: v })}
          />
        )}

        
        {isSmokeLike && (
          <EditDropdown
            label="Management Sensitivity"
            options={DAY_NIGHT_OPTIONS}
            value={draft.dayNight}
            onChange={(v) => set({ dayNight: v })}
          />
        )}
        {isSmokeLike && (
          <EditDropdown
            label="Sensitivity"
            options={SENSITIVITY_OPTIONS}
            value={draft.detectorDaySensitivity}
            onChange={(v) => set({ detectorDaySensitivity: v })}
          />
        )}
        {isSmokeTherm && (
          <EditDropdown
            label="Sensor Disablement"
            options={DISABLE_OPTIONS}
            value={draft.intendedDisChannel}
            onChange={(v) => set({ intendedDisChannel: v })}
          />
        )}
        {isSmokeLike && (
          <EditDropdown
            label="Weekly Trend"
            width={100}
            options={trendOptions(draft.detectorSensitivityWeekCalendar)}
            value={draft.detectorSensitivityWeekCalendar}
            onChange={(v) => set({ detectorSensitivityWeekCalendar: v })}
          />
        )}
      </Group>

      {isSmokeTherm && (
        <Group title="Deactivate Part">
          <EditDropdown
            label="Day"
            options={DISABLE_OPTIONS}
            value={draft.secDay}
            onChange={(v) => set({ secDay: v })}
          />
          <EditDropdown
            label="Night"
            options={DISABLE_OPTIONS}
            value={draft.secNight}
            onChange={(v) => set({ secNight: v })}
          />
          <EditDropdown
            label="Weekly Trend"
            width={100}
            options={trendOptions(draft.detectorMultiSenseWeekCalendar)}
            value={draft.detectorMultiSenseWeekCalendar}
            onChange={(v) => set({ detectorMultiSenseWeekCalendar: v })}
          />
        </Group>
      )}

      <div style={{ display: "flex", gap: 8, margin: "12px 0 0", alignItems: "center" }}>
        <PrimaryButton
          text="Save device settings"
          disabled={!dirty || busy}
          onClick={() => onSave?.(row, changes)}
        />
        <DefaultButton text="Undo" disabled={!dirty || busy} onClick={() => clearDevice?.(row.deviceId)} />
        {dirty && <span style={{ fontSize: 12, color: "#e08a00" }}>Unsaved changes</span>}
      </div>

      <div style={{ fontWeight: 600, margin: "14px 0 0" }}>Zone settings</div>
      {!canBeInZone ? (
        <div style={{ fontSize: 13, color: "#777", marginTop: 4 }}>
          This device type is not added to zones (output devices use output zones instead).
        </div>
      ) : zoneRefs.length === 0 ? (
        <div style={{ fontSize: 13, color: "#777", marginTop: 4 }}>This device is not in any zone.</div>
      ) : (
        zoneRefs.map(({ n, reason }) => (
  <ZoneBlock
    key={n}
    index={n}
    zone={zoneTable[n - 1]}
    reason={reason}
    onSaveZone={onSaveZone}
    busy={busy}
  />
))
      )}

     
    </div>
  );
};

const LogicTable = ({ title, summary, onClose }) => {
  const list = summary?.allocations || [];
  const warnings = summary?.warnings || [];
  return (
    <div
      style={{
        margin: "8px 0",
        padding: 10,
        border: "1px solid #ddd",
        borderRadius: 6,
        background: "#fff",
        maxHeight: 320,
        overflow: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <strong>
          {title} ({list.length} Logic Functions)
        </strong>
        <button className="btn blue" onClick={onClose}>
          Close
        </button>
      </div>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>
            <th>LF</th>
            <th>Source panel</th>
            <th>Source zone</th>
            <th>Output zones</th>
            <th>Delay (s)</th>
            <th>Host panel</th>
          </tr>
        </thead>
        <tbody>
          {list.map((a) => (
            <tr key={`${a.hostPanelNumber}-${a.logicFunctionIndex}`} style={{ borderBottom: "1px solid #eee" }}>
              <td>LF {a.logicFunctionIndex}</td>
              <td>{a.sourcePanelNumber}</td>
              <td>Zone {a.zoneIndex}</td>
              <td>{(a.outputZoneIndices || []).join(", ")}</td>
              <td>{a.delaySeconds}</td>
              <td>{a.hostPanelNumber}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {warnings.length > 0 && (
        <ul style={{ fontSize: 12, color: "#8a5a00", marginTop: 8 }}>
          {warnings.map((w, i) => (
            <li key={i}>
              <b>{w.code}</b>: {w.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const LogicPopup = ({ open, title, summary, onClose }) => {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9998,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 8,
          width: "min(900px, 92vw)",
          maxHeight: "85vh",
          overflow: "auto",
          padding: 16,
        }}
      >
        <LogicTable title={title} summary={summary} onClose={onClose} />
      </div>
    </div>
  );
};

const CloudProLoopTable = ({
  loopNumber,
  panel,
  project,
  workflowDevices = [],
  workflowPanelNumber,
  workflowOptions = { locations: [], zones: [], outputZones: [] },
  onRefresh,
}) => {
  const projectId = project?.project_id;
  const workflowLoopNumber = loopNumber + 1;
  const fileId = panel?.fileId;

  const loopDevices = useMemo(
    () =>
      (Array.isArray(workflowDevices) ? workflowDevices : []).filter(
        (d) =>
          Number(d.panelNumber) === Number(workflowPanelNumber) &&
          Number(d.loopNumber) === Number(workflowLoopNumber)
      ),
    [workflowDevices, workflowPanelNumber, workflowLoopNumber]
  );

  const [edits, setEdits] = useState({});
  const [devEdits, setDevEdits] = useState({});
  const [cfgVersion, setCfgVersion] = useState(0);
  const [filterText, setFilterText] = useState("");
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [bulkLocation, setBulkLocation] = useState("");
  const [bulkRowId, setBulkRowId] = useState("");
  const [bulkOutputZones, setBulkOutputZones] = useState([]);
  const [logicPolicy, setLogicPolicy] = useState("replace");
  const [needsGenerate, setNeedsGenerate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyText, setBusyText] = useState("");
  const [preview, setPreview] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", type: "success" });
  const [showRemovePopup, setShowRemovePopup] = useState(false);
  const [showReplacePopup, setShowReplacePopup] = useState(false);

  const notify = useCallback((msg, type = "success") => {
    setSnack({ open: true, msg, type });
  }, []);

  useEffect(() => {
    setEdits({});
    setDevEdits({});
    setBulkSelected(new Set());
    setLastSelectedIndex(null);
    setPreview(null);
  }, [loopNumber, fileId]);

  const patchDevice = (id, patch) =>
    setDevEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));

  const clearDevice = (id) =>
    setDevEdits((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });
const [cfgOverrides, setCfgOverrides] = useState({});
const [zoneOverrides, setZoneOverrides] = useState({});
useEffect(() => {
  setCfgOverrides({});
    setZoneOverrides({});
}, [fileId]);
const outputZoneMap = useMemo(() => {
  const map = new Map();

  const config = panel?.VT5000_CONFIG;
  const zones =
  config?.outputzones ??
  config?.OutputZones ??
  config?.OutputZone ??
  [];


  zones.forEach((zone, index) => {
    (zone.devices ?? []).forEach((device) => {
      const key = `${Number(device.loop)}-${Number(device.address)}`;

      if (!map.has(key)) {
        map.set(key, []);
      }

      map.get(key).push(index + 1);
    });
  });

  
  return map;
}, [panel]);

  const rows = useMemo(
    () =>
      loopDevices.map((d, i) => {
        const e = edits[d.deviceId] || {};
        const a = d.assignment || {};
        const addr = Number(d.address ?? i + 1);

        const cfgEntry =
          panel?.VT5000_CONFIG?.ArgusTablePr?.[workflowLoopNumber - 1]?.[addr - 1] || {};

       
        const { sourceType, ...dev } = d;
        const devClean = {};
        Object.entries(dev).forEach(([key, v]) => {
          if (v !== undefined && v !== null) devClean[key] = v;
        });
   const raw = {
  ...devClean,
  ...(sourceType || {}),
  ...cfgEntry,
  ...(cfgOverrides[d.deviceId] || {}),   
  deviceId: d.deviceId,
  loopNumber: d.loopNumber,
  address: d.address,
  panelNumber: d.panelNumber,
};

        
        const meta = getDeviceMeta({ ...raw, role: d.role, type: d.type });

        return {
          id: d.deviceId,
          no: d.address ?? i + 1,
          deviceId: d.deviceId,
          cfgLoop: workflowLoopNumber - 1,
          cfgIndex: addr - 1,
          raw,
          meta,
          name: e.name ?? cleanName(d.name),
          locationId: e.locationId ?? (a.locationId || ""),
          rowId: e.rowId ?? (a.rowId || ""),
     zoneText:
  e.zoneText ??
  (
    zoneIndexes(a.outputZones).join(",") ||
    (outputZoneMap.get(
      `${Number(d.loopNumber)}-${Number(d.address)}`
    ) ?? []).join(",")
  ),
          remove: !!e.remove,
          dirty: !!edits[d.deviceId],
          assigned: !!(a.locationId || a.rowId || zoneIndexes(a.outputZones).length),
        };
      }),
   
    [loopDevices, edits, panel, workflowLoopNumber, cfgVersion,outputZoneMap]
  );

  const visibleRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.meta.label, r.locationId, r.rowId, r.zoneText, r.deviceId]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, filterText]);

  const dirtyRows = rows.filter((r) => r.dirty);
  const hasDeviceEdits = Object.keys(devEdits).length > 0;

  const patchRow = (deviceId, patch) =>
    setEdits((prev) => ({ ...prev, [deviceId]: { ...(prev[deviceId] || {}), remove: false, ...patch } }));

  const discardRow = (deviceId) =>
    setEdits((prev) => {
      const next = { ...prev };
      delete next[deviceId];
      return next;
    });

  const knownLocations = useMemo(
    () =>
      [...new Set((workflowDevices || []).map((d) => d.assignment?.locationId).filter(Boolean))].sort(),
    [workflowDevices]
  );

  const locationChoices = useMemo(() => {
    const fromOptions = (workflowOptions?.locations || [])
      .map((l) =>
        typeof l === "object" && l !== null
          ? { id: l.id ?? l.locationId, name: l.name ?? l.label ?? l.id }
          : { id: l, name: l }
      )
      .filter((l) => l.id !== undefined && l.id !== null && l.id !== "");
    if (fromOptions.length) return fromOptions;
    return knownLocations.map((id) => ({ id, name: id }));
  }, [workflowOptions, knownLocations]);

  const allRowChoices = useMemo(
    () =>
      (workflowOptions?.zones || [])
        .map((z) =>
          typeof z === "object" && z !== null
            ? {
                id: z.rowId ?? z.id,
                name: z.name ?? z.label ?? z.rowId ?? z.id,
                locationId: z.locationId ?? z.groupId ?? z.parentId,
                panelNumber: z.panelNumber,
              }
            : { id: z, name: z }
        )
        .filter((z) => z.id !== undefined && z.id !== null && z.id !== ""),
    [workflowOptions]
  );

  const rowsForLocation = (locationId) =>
    allRowChoices.filter(
      (z) =>
        (z.locationId === undefined || String(z.locationId) === String(locationId)) &&
        (z.panelNumber === undefined || Number(z.panelNumber) === Number(workflowPanelNumber))
    );

  const outputZoneChoices = useMemo(
    () =>
      (workflowOptions?.outputZones || [])
        .map((z) =>
          typeof z === "object" && z !== null
            ? {
                index: Number(z.outputZoneIndex ?? z.index ?? z.id),
                name: z.name ?? z.label,
                panelNumber: z.panelNumber,
              }
            : { index: Number(z) }
        )
        .filter(
          (z) =>
            Number.isInteger(z.index) &&
            (z.panelNumber === undefined || Number(z.panelNumber) === Number(workflowPanelNumber))
        ),
    [workflowOptions, workflowPanelNumber]
  );

  const renderLocationSelect = (value, onChange, disabled, emptyLabel) => (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", boxSizing: "border-box", padding: "4px 6px" }}
    >
      <option value="">{emptyLabel}</option>
      {locationChoices.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name || l.id}
        </option>
      ))}
    </select>
  );

  const zoneUsage = useMemo(() => {
    const usage = {};
    (workflowDevices || []).forEach((d) => {
      if (Number(d.panelNumber) !== Number(workflowPanelNumber)) return;
      const own = edits[d.deviceId];
      const list = own?.zoneText != null ? parseZoneText(own.zoneText) : zoneIndexes(d.assignment?.outputZones);
      if (own?.remove) return;
      list.forEach((z) => {
        usage[z] = (usage[z] || 0) + 1;
      });
    });
    return usage;
  }, [workflowDevices, edits, workflowPanelNumber]);

  const buildAssignment = (r) => {
    if (r.remove) return { assignment: { deviceId: r.deviceId, remove: true } };

    const a = { deviceId: r.deviceId };
    if (r.name) a.name = r.name;

    const loc = r.locationId.trim();
    if (loc && locationChoices.length && !locationChoices.some((l) => String(l.id) === loc)) {
      return { error: `"${loc}" is not a known location id. Pick one from the list.` };
    }
    const label = r.name || r.deviceId;
    if (r.meta.noMapping) {
      return { error: `${label}: this device type cannot be mapped` };
    }
    if (!loc) {
      return { error: `${label}: choose a location first. Every mapped device needs one.` };
    }
    a.locationId = loc;

    if (r.meta.isOutput) {
      const zones = parseZoneText(r.zoneText);
      if (zones.some((z) => !Number.isInteger(z) || z < 0)) {
        return { error: `${label}: output zones must be whole numbers, e.g. 10,11` };
      }
      const over = zones.find((z) => (zoneUsage[z] || 0) > MAX_MODULES_PER_OUTPUT_ZONE);
      if (over !== undefined) {
        return { error: `Output zone ${over} would have more than ${MAX_MODULES_PER_OUTPUT_ZONE} modules` };
      }
      if (!zones.length) {
        return { error: `${label}: select at least one output zone for an output module` };
      }
      if (outputZoneChoices.length) {
        const bad = zones.find((z) => !outputZoneChoices.some((c) => c.index === z));
        if (bad !== undefined) {
          return { error: `${label}: output zone ${bad} is not used by the matrix` };
        }
      }

      a.outputZones = zones.map((z) => ({
        panelNumber: Number(workflowPanelNumber),
        outputZoneIndex: z,
      }));
    } else {
      const row = r.rowId.trim();
      if (!row) {
        return { error: `${label}: choose one cause row for this input device` };
      }
      if (allRowChoices.length && !rowsForLocation(loc).some((z) => String(z.id) === row)) {
        return { error: `${label}: row "${row}" does not belong to location "${loc}" on this panel` };
      }

      a.rowId = row;
    }

    return { assignment: a };
  };

  const saveRows = async (list) => {
    if (!list.length) {
      notify("Nothing to save", "info");
      return;
    }
    const assignments = [];
    for (const r of list) {
      const out = buildAssignment(r);
      if (out.error) {
        notify(out.error, "warning");
        return;
      }
      assignments.push(out.assignment);
    }

    try {
      setBusy(true);
      setBusyText("Saving assignments…");
      await cePost("/assignments/save", projectId, { assignments });
      setEdits((prev) => {
        const next = { ...prev };
        list.forEach((r) => delete next[r.deviceId]);
        return next;
      });
      setNeedsGenerate(true);
      await onRefresh?.();
      notify(`Saved ${assignments.length} assignment${assignments.length > 1 ? "s" : ""}`);
    } catch (err) {
   
      notify(apiError(err, "Could not save assignments"), "error");
    } finally {
      setBusy(false);
      setBusyText("");
    }
  };

const saveDeviceSettings = async (row, changes) => {
  const table = panel?.VT5000_CONFIG?.ArgusTablePr;
  const entry = table?.[row.cfgLoop]?.[row.cfgIndex];
  if (!entry) {
    notify("Could not find this device in the config", "error");
    return false;
  }

  const panelContentId =
    panel?.panelContentId ||
    panel?.VT5000_CONFIG?.panelContent_id ||
    project?.panel?.VT5000_CONFIG?.panelContent_id;

  if (!panelContentId) {
    notify("Panel content ID not found for this panel", "error");
    return false;
  }

  const before = {};
  Object.keys(changes).forEach((key) => (before[key] = entry[key]));
  Object.assign(entry, changes);

  try {
    setBusy(true);
    setBusyText("Saving device settings…");
    const res = await Axios.post(`${process.env.REACT_APP_BACKEND_URL}/update_panel_devices`, {
      projectId,
      panelContentId,
      argusTablePr: table,
      email: localStorage.getItem("userEmail"),
    });
    if (!res.data?.success) {
      throw new Error(res.data?.message || res.data?.msg || "The server rejected the change");
    }
  } catch (err) {
    Object.assign(entry, before); 
    setCfgVersion((v) => v + 1);
    notify(apiError(err, err.message || "Could not save device settings"), "error");
    setBusy(false);
    setBusyText("");
    return false;
  }

  setCfgOverrides((p) => ({ ...p, [row.deviceId]: { ...(p[row.deviceId] || {}), ...changes } }));
  clearDevice(row.deviceId);
  setCfgVersion((v) => v + 1);
  setNeedsGenerate(true);

  try {
    const stored = JSON.parse(sessionStorage.getItem("selectedProject"));
    if (stored?.panel?.VT5000_CONFIG?.panelContent_id === panelContentId) {
      stored.panel.VT5000_CONFIG.ArgusTablePr = table;
      sessionStorage.setItem("selectedProject", JSON.stringify(stored));
    }
  } catch (e) {
    console.error("Could not sync selectedProject", e);
  }

  try {
    await onRefresh?.();
  } catch (e) {
    console.error("Refresh after save failed", e);
  }

  notify("Device settings saved");
  setBusy(false);
  setBusyText("");
  return true;
};
const saveZoneSettings = async (zoneIndex, patch) => {
  const config = panel?.VT5000_CONFIG;
  const originalZones = config?.ZoneTable || [];
  const argusTablePr = config?.ArgusTablePr || [];

  const panelContentId =
    panel?.panelContentId ||
    config?.panelContent_id ||
    project?.panel?.VT5000_CONFIG?.panelContent_id;

  if (!panelContentId) {
    notify("Panel content ID not found", "error");
    return;
  }

  const currentZones = originalZones.map((zone, i) => ({
    ...zone,
    ...(zoneOverrides[i] || {}),
  }));

  const updatedZones = currentZones.map((zone, i) =>
    i === zoneIndex ? { ...zone, ...patch } : zone
  );

  // Update the UI immediately.
  setZoneOverrides((previous) => ({
    ...previous,
    [zoneIndex]: {
      ...(previous[zoneIndex] || {}),
      ...patch,
    },
  }));

  try {
    const response = await Axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/update_panel_zones`,
      {
        projectId: project.project_id,
        panelContentId,
        zones: updatedZones,
        argusTablePr,
        email: localStorage.getItem("userEmail"),
      }
    );

    if (!response.data?.success) {
      throw new Error(
        response.data?.message ||
          response.data?.msg ||
          "Failed to update zone settings"
      );
    }

    // Keep the selected project in sync if it is stored in sessionStorage.
    try {
      const stored = JSON.parse(sessionStorage.getItem("selectedProject"));

      if (
        stored?.panel?.VT5000_CONFIG?.panelContent_id === panelContentId
      ) {
        stored.panel.VT5000_CONFIG.ZoneTable = updatedZones;
        sessionStorage.setItem("selectedProject", JSON.stringify(stored));
      }
    } catch (storageError) {
      console.error("Could not sync selectedProject", storageError);
    }

    notify("Zone settings saved");
  } catch (error) {
    // Revert the optimistic UI update on failure.
    setZoneOverrides((previous) => {
      const next = { ...previous };
      delete next[zoneIndex];
      return next;
    });

    notify(
      apiError(error, error.message || "Could not save zone settings"),
      "error"
    );
  }
};
  const clearRow = (r) => {
    patchRow(r.deviceId, { remove: true });
  };

  const handleRemoveConfig = async () => {
    if (!fileId) return;
    setShowRemovePopup(true);
  };

  const confirmRemoveConfig = async () => {
    try {
      setBusy(true);
      setBusyText("Removing config…");
      setShowRemovePopup(false);

      await cePost("/configs/remove", projectId, { fileId });

      setNeedsGenerate(true);
      await onRefresh?.();
      notify("Config removed");
    } catch (err) {
      notify(apiError(err, "Could not remove the config"), "error");
    } finally {
      setBusy(false);
      setBusyText("");
    }
  };

  const guardUnsaved = () => {
    if (dirtyRows.length) {
      notify("Save your changes first", "warning");
      return true;
    }
    if (hasDeviceEdits) {
      notify("Save or undo your device settings first", "warning");
      return true;
    }
    return false;
  };

  const handlePreview = async () => {
    if (guardUnsaved()) return;
    try {
      setBusy(true);
      setBusyText("Building preview…");
      const res = await cePost("/logic/preview", projectId, { logicPolicy });
      setGenerated(null);
      setPreview(res.data);
    } catch (err) {
      notify(apiError(err, "Could not build the preview"), "error");
    } finally {
      setBusy(false);
      setBusyText("");
    }
  };

  const handleGenerate = () => {
    if (guardUnsaved()) return;

    if (logicPolicy === "replace") {
      setShowReplacePopup(true);
      return;
    }
    runGenerate();
  };

  const runGenerate = async () => {
    setShowReplacePopup(false);

    try {
      setBusy(true);
      setBusyText("Generating logic…");

      const result = await cePost("/logic/generate", projectId, { logicPolicy });

      const summary = result.data?.generation?.summary || null;
      const allocations = summary?.allocations || [];

      writeGeneratedLogicToProject(allocations, workflowPanelNumber, projectId);

      setPreview(null);
      setNeedsGenerate(false);

      await onRefresh?.();
      window.dispatchEvent(new Event("project-refreshed"));

      setGenerated(summary);
      notify("Logic Functions generated");
    } catch (err) {
      console.error("Logic generation failed:", err);
      notify(apiError(err, "Could not generate logic"), "error");
    } finally {
      setBusy(false);
      setBusyText("");
    }
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleExport = async () => {
    if (!fileId) return notify("Select a panel first", "warning");
    if (guardUnsaved()) return;
    if (needsGenerate) return notify("Generate logic before exporting", "warning");
    try {
      setBusy(true);
      setBusyText("Exporting config…");
    
      const res = await cePost("/configs/export", projectId, { fileId, download: false });
      const result = res.data;
      if (result?.success === false) throw new Error(result.message || result.msg || "Export failed");
      const config = prepareCfgExport(result?.content, workflowPanelNumber);
    
      const panelVersion = sessionStorage.getItem("panelVersion1") || "2.0.F7";
      const buffer = new ConfigFileGenerator(config, panelVersion).generateConfigFile();
      const filename = "VELOXCONFIG.CFG";
      triggerDownload(new Blob([new Uint8Array(buffer)], { type: "application/octet-stream" }), filename);
      notify("Config exported");
    } catch (err) {
      notify(apiError(err, err.message || "Could not export the config"), "error");
    } finally {
      setBusy(false);
      setBusyText("");
    }
  };

  const exportRows = () =>
    rows.map((r, i) => [
      i + 1,
      r.deviceId,
      r.name,
      r.meta.label,
      r.locationId,
      r.meta.isOutput ? "" : r.rowId,
      r.meta.isOutput ? r.zoneText : "",
    ]);
  const exportHead = ["No", "Device ID", "Name", "Type", "Location", "Row", "Output zones"];

  const exportCSV = () => {
    const csv = Papa.unparse([
      ["Project", project?.name || ""],
      ["Panel", panel?.displayName || ""],
      ["Loop", workflowLoopNumber],
      ["Generated", new Date().toLocaleString()],
      [],
      exportHead,
      ...exportRows(),
    ]);
    triggerDownload(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
      `${project?.name || "project"}_${panel?.displayName || "panel"}_loop${workflowLoopNumber}.csv`
    );
  };

  const exportPDF = () => {
    if (!rows.length) return notify("No devices to export", "warning");
    const doc = new jsPDF();
    doc.text(`${panel?.displayName || "Panel"} - Loop ${workflowLoopNumber}`, 14, 12);
    autoTable(doc, { head: [exportHead], body: exportRows(), startY: 20, styles: { fontSize: 8 } });
    doc.save(`loop_${workflowLoopNumber}.pdf`);
  };

  const applyBulkChanges = () => {
    const loc = bulkLocation.trim();
    const rowId = bulkRowId.trim();
    const zones = bulkOutputZones.map(Number).filter(Number.isInteger);

    if (!loc && !rowId && !zones.length) {
      return notify("Select a location, row, or output zone to apply", "warning");
    }

    const selectedRows = rows.filter((r) => bulkSelected.has(r.id) && !r.meta.noMapping && !r.remove);

    if (!selectedRows.length) {
      return notify("No mappable devices selected", "warning");
    }

    const inputRows = selectedRows.filter((r) => !r.meta.isOutput);
    const outputRows = selectedRows.filter((r) => r.meta.isOutput);

    if (loc && locationChoices.length && !locationChoices.some((l) => String(l.id) === loc)) {
      return notify(`"${loc}" is not a valid location`, "warning");
    }

    if (rowId) {
      if (!inputRows.length) {
        return notify("No input devices are selected for the Row assignment", "warning");
      }

      const targetLocation = loc || inputRows[0].locationId;

      if (!targetLocation) {
        return notify("Select a location before applying the input row", "warning");
      }

      const invalid = inputRows.find((r) => {
        const deviceLocation = loc || r.locationId;
        return !rowsForLocation(deviceLocation).some((z) => String(z.id) === rowId);
      });

      if (invalid) {
        return notify(`Row "${rowId}" is not valid for all selected input devices`, "warning");
      }
    }

    if (zones.length) {
      if (!outputRows.length) {
        return notify("No output devices are selected for Output Zone assignment", "warning");
      }

      if (outputZoneChoices.length) {
        const invalidZone = zones.find((z) => !outputZoneChoices.some((c) => c.index === z));

        if (invalidZone !== undefined) {
          return notify(`Output zone ${invalidZone} is not used by the matrix`, "warning");
        }
      }

      for (const zone of zones) {
        const currentUsage = zoneUsage[zone] || 0;
        const additionalUsage = outputRows.filter((r) => !parseZoneText(r.zoneText).includes(zone)).length;

        if (currentUsage + additionalUsage > MAX_MODULES_PER_OUTPUT_ZONE) {
          return notify(
            `Output zone ${zone} would exceed the maximum of ${MAX_MODULES_PER_OUTPUT_ZONE} modules`,
            "warning"
          );
        }
      }
    }

    setEdits((prev) => {
      const next = { ...prev };

      selectedRows.forEach((r) => {
        const patch = {
          ...(next[r.id] || {}),
          remove: false,
        };

        if (loc) {
          patch.locationId = loc;
          if (!r.meta.isOutput) {
            patch.rowId = "";
          }
        }

        if (!r.meta.isOutput && rowId) {
          patch.rowId = rowId;
          patch.zoneText = "";
        }

        if (r.meta.isOutput && zones.length) {
          patch.zoneText = zones.join(",");
          patch.rowId = "";
        }

        next[r.id] = patch;
      });

      return next;
    });

    notify(`Applied bulk changes to ${selectedRows.length} selected device(s)`);
  };

  const handleBulkCheckboxClick = (row, e) => {
    const index = visibleRows.findIndex((r) => r.id === row.id);
    if (index === -1) return;

    setBulkSelected((prev) => {
      const next = new Set(prev);

      if (e.shiftKey && lastSelectedIndex !== null) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);

        for (let i = start; i <= end; i++) {
          const r = visibleRows[i];
          if (r && !r.meta.noMapping && !r.remove) next.add(r.id);
        }
      } else {
        if (next.has(row.id)) next.delete(row.id);
        else next.add(row.id);
      }

      return next;
    });

    setLastSelectedIndex(index);
  };

  const inputStyle = (changed) => ({
    width: "100%",
    boxSizing: "border-box",
    padding: "4px 6px",
    border: changed ? "1px solid #e08a00" : "1px solid #ccc",
  });

  const selectSx = {
    fontSize: 12,
    height: 28,
    "& .MuiSelect-select": { padding: "2px 24px 2px 8px !important" },
    "& .MuiSvgIcon-root": { fontSize: 18 },
  };

  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => bulkSelected.has(r.id));

  const columns = [
    {
      name: (
        <input
          type="checkbox"
          checked={allVisibleSelected}
          onChange={(e) => {
            if (e.target.checked) {
              setBulkSelected(
                new Set(visibleRows.filter((r) => !r.meta.noMapping && !r.remove).map((r) => r.id))
              );
            } else {
              setBulkSelected(new Set());
            }
            setLastSelectedIndex(null);
          }}
          aria-label="Select all visible devices"
        />
      ),
      width: "50px",
      cell: (r) => (
        <input
          type="checkbox"
          checked={bulkSelected.has(r.id)}
          disabled={r.meta.noMapping || r.remove}
          aria-label={`Select ${r.name || r.deviceId}`}
          onChange={() => {}}
          onClick={(e) => handleBulkCheckboxClick(r, e)}
          title="Shift + click to select a range"
        />
      ),
    },
    { name: "No.", selector: (r) => r.no, width: "120px", sortable: true },
    {
      name: "Device ID",
      selector: (r) => r.deviceId,
      width: "120px",
      sortable: true,
    },
    {
      name: "Type",
      width: "190px",
      cell: (r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {r.meta.icon && <img src={r.meta.icon} alt="" style={{ width: 20, height: 20 }} />}
          <span>{r.meta.label}</span>
        </div>
      ),
    },
    {
      name: "Device name",
      width: "190px",
      cell: (r) => (
        <input
          type="text"
          value={r.name}
          maxLength={20}
          title={r.name}
          disabled={r.remove}
          onChange={(e) => patchRow(r.id, { name: e.target.value.slice(0, 20) })}
          style={inputStyle(false)}
        />
      ),
    },
    {
      name: "Location",
      width: "170px",
      cell: (r) =>
        r.meta.noMapping ? (
          <span>-</span>
        ) : locationChoices.length ? (
          renderLocationSelect(
            r.locationId,
            (v) => patchRow(r.id, { locationId: v, rowId: "" }),
            r.remove,
            "-- Select location --"
          )
        ) : (
          <input
            value={r.locationId}
            placeholder="Location id (no list available)"
            disabled={r.remove}
            onChange={(e) => patchRow(r.id, { locationId: e.target.value })}
            style={inputStyle(false)}
          />
        ),
    },
    {
      name: "Row (input)",
      width: "170px",
      cell: (r) =>
        r.meta.isOutput || r.meta.noMapping ? (
          <span>-</span>
        ) : allRowChoices.length ? (
          <Select
            displayEmpty
            fullWidth
            size="small"
            disabled={r.remove || !r.locationId}
            value={r.rowId || ""}
            onChange={(e) => patchRow(r.id, { rowId: e.target.value })}
            renderValue={(v) => {
              if (!v) {
                return (
                  <span style={{ color: "#888" }}>
                    {r.locationId ? "-- Select row --" : "Choose location first"}
                  </span>
                );
              }
              const found = rowsForLocation(r.locationId).find((z) => String(z.id) === String(v));
              return found?.name || v;
            }}
            sx={selectSx}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {rowsForLocation(r.locationId).map((z) => (
              <MenuItem key={z.id} value={z.id} dense sx={{ fontSize: 12 }}>
                {z.name || z.id}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <input
            value={r.rowId}
            placeholder="Row id (no list available)"
            disabled={r.remove}
            onChange={(e) => patchRow(r.id, { rowId: e.target.value })}
            style={inputStyle(false)}
          />
        ),
    },
    {
      name: "Output zones",
      width: "190px",
      cell: (r) => {
        if (!r.meta.isOutput) return <span>-</span>;

        if (outputZoneChoices.length) {
          const selected = parseZoneText(r.zoneText)
            .filter((z) => Number.isInteger(z))
            .map(String);

          return (
            <Select
              multiple
              displayEmpty
              fullWidth
              size="small"
              disabled={r.remove}
              value={selected}
              onChange={(e) => {
                const v = e.target.value;
                const list = typeof v === "string" ? v.split(",") : v;
                patchRow(r.id, { zoneText: list.join(",") });
              }}
              renderValue={(s) => (s.length ? s.join(", ") : "-- Select zones --")}
              sx={selectSx}
              MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
            >
              {outputZoneChoices.map((c) => {
                const v = String(c.index);
                const isSel = selected.includes(v);
                const full = !isSel && (zoneUsage[c.index] || 0) >= MAX_MODULES_PER_OUTPUT_ZONE;
                return (
                  <MenuItem key={v} value={v} disabled={full}>
                    <Checkbox checked={isSel} />
                    <ListItemText primary={`${c.name || `Output zone ${c.index}`}${full ? " - full" : ""}`} />
                  </MenuItem>
                );
              })}
            </Select>
          );
        }

        const full = parseZoneText(r.zoneText).filter((z) => (zoneUsage[z] || 0) > MAX_MODULES_PER_OUTPUT_ZONE);
        return (
          <div style={{ width: "100%" }}>
            <input
              value={r.zoneText}
              placeholder="e.g. 10,11"
              disabled={r.remove}
              onChange={(e) => patchRow(r.id, { zoneText: e.target.value })}
              style={{ ...inputStyle(false), border: full.length ? "1px solid red" : "1px solid #ccc" }}
              title={`Up to ${MAX_MODULES_PER_OUTPUT_ZONE} modules per output zone`}
            />
            {full.length > 0 && (
              <div style={{ color: "red", fontSize: 11 }}>Zone {full.join(", ")} is over capacity</div>
            )}
          </div>
        );
      },
    },
    {
      name: "Status",
      width: "110px",
      cell: (r) =>
        r.remove ? (
          <span style={{ color: "#b00020" }}>Will clear</span>
        ) : r.dirty ? (
          <span style={{ color: "#e08a00" }}>Unsaved</span>
        ) : r.assigned ? (
          <span style={{ color: "#2e7d32" }}>Assigned</span>
        ) : (
          <span style={{ color: "#777" }}>Not assigned</span>
        ),
    },
    {
      name: "Actions",
      width: "230px",
      cell: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn csv" disabled={!r.dirty || busy} onClick={() => saveRows([r])}>
            Save
          </button>
          <button className="btn blue" disabled={!r.dirty || busy} onClick={() => discardRow(r.id)}>
            Undo
          </button>
          <button
            className="btn pdf"
            disabled={busy || r.meta.noMapping || (!r.assigned && !r.dirty) || r.remove}
            onClick={() => clearRow(r)}
            title="Remove this device's mapping"
          >
            Clear
          </button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginTop: "15px" }}>
      <div className="table-header">
        <h6 className="table-title">
          {panel?.displayName || "Panel"} / Loop {workflowLoopNumber}
          <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 8, color: "#666" }}>
            {rows.length} devices, revision {getRevision() ?? "-"}
          </span>
        </h6>

        <div className="table-actions" style={{ flexWrap: "wrap", gap: 8 }}>
          <input
            type="text"
            placeholder="Search..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="search-input"
          />

          <button className="btn add" disabled={busy || !dirtyRows.length} onClick={() => saveRows(dirtyRows)}>
            Save all changes{dirtyRows.length ? ` (${dirtyRows.length})` : ""}
          </button>
          <button className="btn blue" disabled={!dirtyRows.length} onClick={() => setEdits({})}>
            Discard all
          </button>

          <button onClick={exportCSV} className="btn csv">
            <FaFileCsv /> CSV
          </button>
          <button onClick={exportPDF} className="btn pdf">
            <FaRegFilePdf /> PDF
          </button>
        </div>
      </div>

      {bulkSelected.size > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            margin: "8px 0",
            padding: "10px",
            flexWrap: "wrap",
            border: "1px solid #ddd",
            borderRadius: 6,
            background: "#f8f8f8",
          }}
        >
          <strong style={{ paddingTop: 6 }}>{bulkSelected.size} selected</strong>

          <div style={{ minWidth: 190 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Location</div>

            {locationChoices.length ? (
              renderLocationSelect(bulkLocation, setBulkLocation, false, "-- Select location --")
            ) : (
              <input
                value={bulkLocation}
                placeholder="Location id"
                onChange={(e) => setBulkLocation(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", padding: "5px 6px" }}
              />
            )}
          </div>

          <div style={{ minWidth: 210 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Row (Input)</div>

            <Select
              displayEmpty
              fullWidth
              size="small"
              disabled={!bulkLocation}
              value={bulkRowId}
              onChange={(e) => setBulkRowId(e.target.value)}
              renderValue={(v) => {
                if (!v) {
                  return (
                    <span style={{ color: "#888" }}>
                      {bulkLocation ? "-- Select row --" : "Choose location first"}
                    </span>
                  );
                }
                const found = rowsForLocation(bulkLocation).find((z) => String(z.id) === String(v));
                return found?.name || v;
              }}
              sx={selectSx}
              MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
            >
              {rowsForLocation(bulkLocation).map((z) => (
                <MenuItem key={z.id} value={z.id} dense sx={{ fontSize: 12 }}>
                  {z.name || z.id}
                </MenuItem>
              ))}
            </Select>

            <div style={{ fontSize: 11, color: "#777", marginTop: 3 }}>Applies only to input devices</div>
          </div>

          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Output Zones</div>

            {outputZoneChoices.length ? (
              <Select
                multiple
                displayEmpty
                fullWidth
                size="small"
                value={bulkOutputZones.map(String)}
                onChange={(e) => {
                  const v = e.target.value;
                  setBulkOutputZones(typeof v === "string" ? v.split(",") : v);
                }}
                renderValue={(s) => (s.length ? s.join(", ") : "-- Select zones --")}
                sx={selectSx}
                MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
              >
                {outputZoneChoices.map((c) => {
                  const v = String(c.index);
                  const isSel = bulkOutputZones.map(String).includes(v);
                  const full = !isSel && (zoneUsage[c.index] || 0) >= MAX_MODULES_PER_OUTPUT_ZONE;
                  return (
                    <MenuItem key={v} value={v} disabled={full} dense sx={{ fontSize: 12, py: 0 }}>
                      <Checkbox checked={isSel} size="small" sx={{ p: 0.5 }} />
                      <ListItemText primary={`${c.name || `Output zone ${c.index}`}${full ? " - full" : ""}`} />
                    </MenuItem>
                  );
                })}
              </Select>
            ) : (
              <input
                value={bulkOutputZones.join(",")}
                placeholder="e.g. 10,11"
                onChange={(e) =>
                  setBulkOutputZones(
                    e.target.value
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean)
                  )
                }
                style={{ width: "100%", boxSizing: "border-box", padding: "5px 6px" }}
              />
            )}

            <div style={{ fontSize: 11, color: "#777", marginTop: 3 }}>Applies only to output devices</div>
          </div>

          <div style={{ paddingTop: 12, margin: "5px 0" }}>
            <button className="btn add" onClick={applyBulkChanges} disabled={busy || bulkSelected.size === 0}>
              Apply to Selected
            </button>
            <button
              style={{ margin: "5px" }}
              className="btn blue"
              onClick={() => {
                setBulkSelected(new Set());
                setLastSelectedIndex(null);
                setBulkLocation("");
                setBulkRowId("");
                setBulkOutputZones([]);
              }}
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          margin: "10px 0",
          padding: "8px 10px",
          border: "1px solid #ddd",
          borderRadius: 6,
          background: "#fafafa",
        }}
      >
        <button className="btn pdf" disabled={busy || !fileId} onClick={handleRemoveConfig}>
          Remove config
        </button>

        <span style={{ margin: "0 6px", color: "#bbb" }}>|</span>

        <label style={{ fontSize: 13 }}>
          Logic policy{" "}
          <select
            value={logicPolicy}
            onChange={(e) => {
              setLogicPolicy(e.target.value);
              setPreview(null);
            }}
            disabled={busy}
          >
            <option value="replace">Replace (rebuild from matrix)</option>
            <option value="preserve">Preserve (add in free slots)</option>
          </select>
        </label>
        <button className="btn csv" disabled={busy} onClick={handlePreview}>
          Preview logic
        </button>
        <button className="btn add" disabled={busy} onClick={handleGenerate}>
          Generate logic
        </button>
        <button className="btn csv" disabled={busy || !fileId} onClick={handleExport}>
          Export config
        </button>

        <span style={{ fontSize: 12, color: needsGenerate ? "#e08a00" : "#2e7d32" }}>
          {needsGenerate ? "Generate again before export" : "Logic is up to date"}
        </span>
      </div>

      <LogicPopup
        open={!!preview}
        title={`Logic preview (${logicPolicy})`}
        summary={preview?.summary}
        onClose={() => setPreview(null)}
      />
      <LogicPopup
        open={!!generated}
        title="Generated logic"
        summary={generated}
        onClose={() => setGenerated(null)}
      />

      <div style={{ width: "100%", overflowX: "auto" }}>
        <DataTable
          columns={columns}
          data={visibleRows}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30]}
          highlightOnHover
          striped
          fixedHeader
          fixedHeaderScrollHeight="490px"
          expandableRows
          expandableRowsComponent={DeviceDetails}
         expandableRowsComponentProps={{
  zoneTable: (panel?.VT5000_CONFIG?.ZoneTable || []).map((zone, index) => ({
    ...zone,
    ...(zoneOverrides[index] || {}),
  })),
  weeklyTrends: panel?.VT5000_CONFIG?.WeeklyTrends,
  onSave: saveDeviceSettings,
  onSaveZone: saveZoneSettings,
  busy,
  devEdits,
  patchDevice,
  clearDevice,
}}
          noDataComponent={
            <div style={{ padding: 20 }}>
              {workflowDevices.length === 0
                ? "No devices yet. Import a config to get started."
                : "No devices on this loop."}
            </div>
          }
        />
      </div>

      {showReplacePopup && (
        <div className="popup">
          <div className="popup-content">
            <h3>Replace Logic Functions?</h3>

            <p>
              Replace rebuilds all Logic Functions from the matrix. Existing Logic Functions on this panel will be
              overwritten. Continue?
            </p>

            <div className="popup-actions">
              <button onClick={runGenerate} disabled={busy} className="add-btn">
                Replace
              </button>
              <button onClick={() => setShowReplacePopup(false)} disabled={busy} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showRemovePopup && (
        <div className="popup">
          <div className="popup-content">
            <h3>Remove Config?</h3>

            <p>
              Are you sure you want to remove <strong>{panel?.displayName || "this config"}</strong> from the
              project?
            </p>

            <div className="popup-actions">
              <button onClick={confirmRemoveConfig} disabled={busy} className="add-btn">
                {busy ? "Removing..." : "Remove"}
              </button>
              <button onClick={() => setShowRemovePopup(false)} disabled={busy} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Backdrop open={busy} sx={{ color: "#fff", zIndex: 9999, flexDirection: "column", gap: 1 }}>
        <CircularProgress color="inherit" />
        {busyText && <div>{busyText}</div>}
      </Backdrop>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.type} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CloudProLoopTable;